import { useCallback, useMemo } from "react";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { DataField } from "@iventis/domain-model/model/dataField";
import { sortDataFieldsOnDelete, replaceItemInArray } from "@iventis/utilities";
import { IDataFieldServices, IDataFieldService, IDataFieldListItemService } from "@iventis/datafield-editor";
import { stripStyleValuesWithRemovedDataFieldReferences, stripStyleValuesWithRemovedListItemReference } from "@iventis/map-engine/src/utilities/style-helpers";

interface UseDataFieldInterceptorOptions {
    dataFieldsService: IDataFieldService;
    dataFieldListItemsService: IDataFieldListItemService;
    setPartialLayer: (updator: Partial<MapLayer> | ((layer: MapLayer) => Partial<MapLayer>), forceSave?: boolean) => void;
    existsOnServer: boolean;
    layerId: string;
    orderedDataFieldCount: number;
}

/**
 * Creates a service interceptor that wraps data field CRUD operations with local layer state updates.
 * Fixes: preserves data field `order` from layer state instead of allowing stale editor state to overwrite it.
 */
export const useDataFieldInterceptor = ({
    dataFieldsService,
    dataFieldListItemsService,
    setPartialLayer,
    existsOnServer,
    layerId,
    orderedDataFieldCount,
}: UseDataFieldInterceptorOptions): IDataFieldServices => {
    const postDataField = useCallback(
        async (dataField: DataField) => {
            const orderedDatafield = { ...dataField, order: orderedDataFieldCount + 1 };
            setPartialLayer((layer) => ({ ...layer, dataFields: [...layer.dataFields, orderedDatafield] }), true);
            await dataFieldsService.postDataField(orderedDatafield, layerId);
        },
        [dataFieldsService, setPartialLayer, layerId, orderedDataFieldCount]
    );

    const putDataField = useCallback(
        async (dataField: DataField) => {
            if (existsOnServer) {
                setPartialLayer((layer) => {
                    const originalDataField = layer.dataFields.find((df) => df.id === dataField.id);
                    // Preserve the current order from layer state — prevents stale editor state from reverting reorder
                    const updatedDataField: DataField = { ...originalDataField, ...dataField, order: originalDataField?.order ?? dataField.order };
                    return { dataFields: replaceItemInArray(layer.dataFields, updatedDataField) };
                });
                await dataFieldsService.putDataField(dataField, layerId);
            }
        },
        [dataFieldsService, setPartialLayer, existsOnServer, layerId]
    );

    const deleteDataField = useCallback(
        async (dataFieldId: string, _layerId: string) => {
            setPartialLayer((layer) => {
                const newDataFields = sortDataFieldsOnDelete(layer.dataFields, dataFieldId);
                return {
                    ...stripStyleValuesWithRemovedDataFieldReferences(layer, dataFieldId),
                    dataFields: newDataFields,
                    tooltipDataFieldIds: layer.tooltipDataFieldIds?.filter((id) => id !== dataFieldId),
                };
            }, true);
            await dataFieldsService.deleteDataField(dataFieldId, _layerId);
        },
        [dataFieldsService, setPartialLayer]
    );

    const deleteDataFieldListItem = useCallback(
        async (listItemId: string, dataFieldId: string, _layerId: string) => {
            setPartialLayer((layer) => stripStyleValuesWithRemovedListItemReference(layer, listItemId), true);
            await dataFieldListItemsService.deleteDataFieldListItem(listItemId, dataFieldId, _layerId);
        },
        [dataFieldListItemsService, setPartialLayer]
    );

    return useMemo(
        () => ({
            dataFieldsService: {
                ...dataFieldsService,
                postDataField,
                putDataField,
                deleteDataField,
            },
            dataFieldListItemsService: {
                ...dataFieldListItemsService,
                deleteDataFieldListItem,
            },
        }),
        [dataFieldsService, dataFieldListItemsService, postDataField, putDataField, deleteDataField, deleteDataFieldListItem]
    );
};
